/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Render the invitation dialog for classes, live sessions and homework.
 * @state-owner dialog-local React state
 * @allowed-imports React; dataset and workspace stores; shared components, selectors and hooks
 * @forbidden direct API client; real QR encoding
 * @public-api classrooms module
 * @side-effects clipboard toasts and link rotation
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { useState } from 'react';
import { VispoAnimatedMark } from '../../../shared/components/BrandMarks';
import { ModalDocument } from '../../../shared/components/Modal';
import { QrCode } from '../../../shared/components/QrCode';
import { CopyIcon, InfoIcon } from '../../../shared/components/VispoIcon';
import { RUN_MODE_LABEL } from '../../../shared/lib/demo-data';
import { findRun, runName } from '../../../shared/lib/selectors';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';

export function QrInviteDialog({ kind, id }: { kind: 'class' | 'run'; id: string | number }) {
  const t = useTranslator();
  const classes = useDatasetStore((state) => state.classes);
  const activities = useDatasetStore((state) => state.activities);
  const runs = useDatasetStore((state) => state.runs);
  const notify = useWorkspaceUiStore((state) => state.notify);
  const [rotation, setRotation] = useState(0);

  const target = (() => {
    if (kind === 'class') {
      const classroom = classes[Number(id)];
      return {
        name: classroom.name,
        sub: classroom.subtitle,
        url: `https://vispo.app/join/${classroom.code.toLowerCase()}8f2a17`,
        title: t('班級 QR 邀請'),
      };
    }
    const run = findRun(runs, String(id));
    if (!run) return { name: '', sub: '', url: '', title: '' };
    return {
      name: runName(activities, run),
      sub: `${classes[run.classIndex].name} · ${t(RUN_MODE_LABEL[run.mode])}`,
      url: `https://vispo.app/join/${run.mode === 'live' ? 'live_' : 'hw_'}${run.id}c4d91b`,
      title: run.mode === 'live' ? t('課堂 QR 邀請') : t('作業 QR 連結'),
    };
  })();

  const url = rotation ? target.url.slice(0, -6) + String(rotation).padStart(6, '0') : target.url;

  return (
    <ModalDocument
      footer={
        <>
          <div className="sp" />
          <button className="bt" type="button" onClick={() => notify(t('已複製連結'))}>
            {t('複製')}
          </button>
          <button
            className="bt"
            type="button"
            onClick={() => {
              setRotation((current) => current + 1);
              notify(t('已重置連結，舊連結立刻失效'));
            }}
          >
            {t('重置連結')}
          </button>
        </>
      }
      maxWidth={460}
      subtitle={`${target.name} · ${target.sub}`}
      title={target.title}
    >
      <div className="vqrbox">
        <div className="vqrbrand">
          <VispoAnimatedMark className="vqrlogo" />
          {target.name}
        </div>
        <div className="vqrimg">
          <QrCode seed={url} />
        </div>
      </div>
      <div className="vqrurl">
        <input readOnly value={url} />
        <button
          className="uib"
          title={t('複製連結')}
          type="button"
          onClick={() => notify(t('已複製連結'))}
        >
          <CopyIcon />
        </button>
      </div>
      <div className="vqrscope">
        {kind === 'class'
          ? t('這是班級加入連結，學生加入後所有活動都會出現在他的清單。')
          : t('這是這一份活動專用的連結，跟班級加入連結不一樣，只會進到這一份。')}
      </div>
      <div className="vqrwarn">
        <InfoIcon />
        {t('連結外流時請立即重置。')}
      </div>
    </ModalDocument>
  );
}
